import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";

import {
	Modal,
	Layout,
	Button,
	ErrorBanner,
	ProgressIndicator,
	Icon,
	Tooltip
} from "rhombus-react";

import { loadAudioSpecs } from "reducers/creatives/VAST";
import { TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { EmptyDash } from "widgets/grid/cells/EmptyDash";

import "./AudioSpecsModal.css";

const orderedFields = [ // We can map this array to generate the table rows in the correct order
	{
		name: "url",
		label: "File",
	},
	{
		name: "duration",
		label: "Duration",
	},
	{
		name: "filesize",
		label: "File Size",
	},
	{
		name: "audioBitrate",
		label: "Audio Bitrate",
	},
	{
		name: "audioSamplerate",
		label: "Audio Sample Rate",
	},
	{
		name: "audioCodec",
		label: "Audio Codec",
	},
	{
		name: "audioGain",
		label: "Audio Level",
	},
];

export default function AudioSpecsModal({
	audioId,
	type,
	returnPath,
}){
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const [audioDetail, setAudioDetail] = useState({ specs:{} });
	let header = <p className="info">The following are the original file specs.  Publisher-specific transcodes are generated when the VAST creative is added to a placement in Campaign Manager.</p>;
	let errorMessages;
	let errorObj = {};
	let name;

	useEffect(() => {
		if (!audioId || audioId === -1) { return; }
		if (type === TYPES.CREATIVES) {
			dispatch(loadAudioSpecs(audioId))
				.then((resp) => {
					setIsLoading(false);
					if(resp.success){
						setAudioDetail(resp.payload.data);
					}
				});
		}
	}, [audioId]);


	if (type === TYPES.CREATIVES) {
		name = audioDetail?.creative?.name || "";
	} else {
		name = audioDetail?.name || "";
	}

	function closeModal() {
		dispatch(push(returnPath));
	}

	if (audioDetail.errors) {
		errorObj = Object.assign({}, ...(audioDetail.errors.map((err) => {
			let idx = orderedFields.findIndex(fld => err.field === fld.name);

			if (idx===-1) {
				return;
			}

			return {
				[err.field]: {
					label: orderedFields[idx].label,
					error: err.error,
					message: `${orderedFields[idx].label} - ${err.error}`,
				},
			};
		})));

		errorMessages = audioDetail?.errors?.map(({ field, error }) => {
			let idx = orderedFields.findIndex(fld =>field===fld.name);
			if (idx === -1) {
				return `${field} - ${error}`;
			}
			return `${orderedFields[idx].label} - ${error}`;
		});

		if (errorMessages?.length) {
			header =  (
				<ErrorBanner
					title="Audio file does not meet the following VAST requirements"
					messages={errorMessages}
				/>
			);
		}
	}

	function ModalContent(){
		let rows = orderedFields.map(({ name, label }) => {
			let value = audioDetail.specs[name];
			let errorClass = "";

			if (/filesize/gi.test(name)) {
				value = formatBytes(value, 1);
			}
			if (/url/gi.test(name)) {
				value = <div className="audio-url"><a title={audioDetail.specs[name]} href={audioDetail.specs[name]} target="_BLANK" download>Download Asset</a></div>;
			}

			if (!value || value === "") { value = <EmptyDash />; }

			if (errorObj[name]) {
				errorClass = "error";
				value = (
					<div className="value-with-error">
						<span className="value">{value}</span>
						<Tooltip description={errorObj[name].error} direction="right" theme="red">
							<Icon name="clearCircle" className="valueError" />
						</Tooltip>
					</div>
				);
			}

			return (
				<tr key={name}>
					<td className={`name-col ${errorClass}`}>{label}</td>
					<td className={`spec-value ${errorClass}`}>{value}</td>
				</tr>
			);
		});

		return (
			<div>
				{!isLoading ? header : null}
				<div className="specs-container">
					<ProgressIndicator isLoading={isLoading} />
					<table className="basic-table audio-specs-table">
						<tbody>
							{rows}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	function ModalFooter() {
		return (
			<Layout.ModalFooter>
				<Button onClick={closeModal}>
					Close
				</Button>
			</Layout.ModalFooter>
		);
	}

	let modalProps = {
		title: `Audio Specs: ${name}`,
		setAppTitle: false,
		type: "modal",
		className: "audio-specs-modal",

		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},

		renderContent: ModalContent,
		renderFooter: ModalFooter,

		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}

AudioSpecsModal.displayName = "AudioSpecsModal";
AudioSpecsModal.defaultProps = {
	audioId:-1,
	type:TYPES.CREATIVES,
};

AudioSpecsModal.propTypes = {
	type:PropTypes.oneOf([TYPES.ASSETS, TYPES.CREATIVES]).isRequired,
	audioId:PropTypes.number.isRequired,
};
